import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {useSelector} from '../../reduxHooks';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Button, Card, Dialog } from '@betareader/design-system';
import styled from 'styled-components';
import Icon from '../AppIcon';

import { useCredits, useCreditsConfig, CreditPack } from 'src/hooks/useCredits';
import { useHostAppProxy } from 'src/contexts/HostAppProxyContext';

const appleProductIdForPack = (pack: CreditPack) =>
  `io.betareader.credits.${pack.credits}`;

const CreditDialog = styled(Dialog)`
  max-width: 720px;
`;

const Columns = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: stretch;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const Column = styled(Card)`
  flex: 1;
  min-width: 240px;
  margin: 0;
  display: flex;
  flex-direction: column;
`;

const ColumnHeading = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
`;

const PackRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
`;

const Spacer = styled.div`
  flex: 1;
`;

type OutOfCreditsModalProps = {
  open: boolean;
  onClose: () => void;
};

const OutOfCreditsModal: React.FC<OutOfCreditsModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { data: config } = useCreditsConfig();
  const { data: credits } = useCredits();
  const queryClient = useQueryClient();
  const { settings, purchaseProduct } = useHostAppProxy();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userProfile = useSelector((state: any) => state.user.userProfile);

  const packs = config?.packs || [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const paddle = (window as any).Paddle;
  // Credit the account the SERVER resolves for this user (owner-first), echoed
  // from /billing/credits — NOT a client-derived membership, which can differ
  // for multi-account users and land the credits on the wrong account.
  const accountId = credits?.accountId;
  // Ready to open checkout once Paddle is loaded and we know which account to
  // credit. The per-pack Paddle product id is checked separately in `purchasable`.
  const useNativePurchases = Boolean(settings.isWrapped);
  const canCheckout = Boolean(!useNativePurchases && paddle && accountId);

  React.useEffect(() => {
    const status = settings.nativePurchaseEvent?.data?.status;
    if (status === 'success') {
      [1000, 3000, 7000, 15000].forEach(delay =>
        setTimeout(
          () => queryClient.invalidateQueries({
            queryKey: ['billingCredits']
          }),
          delay
        )
      );
    } else if (status === 'cancelled') {
      toast.info(t('Cancelled', 'Cancelled'));
    } else if (status === 'error') {
      toast.error(
        t('PurchaseFailed', 'The purchase could not be completed.')
      );
    }
  }, [settings.nativePurchaseEvent, queryClient, t]);

  const goToSubscription = () => {
    onClose();
    history.push('/products');
  };

  const buyPack = (pack: CreditPack) => {
    if (useNativePurchases) {
      if (settings.purchaseMode === 'ios_iap') {
        purchaseProduct(appleProductIdForPack(pack));
      }
      return;
    }
    if (!pack.paddleProductId || !canCheckout) return;
    try {
      paddle.Checkout.open({
        // The pack that gets granted is derived server-side from the product
        // Paddle charges for; passthrough only names the account to credit.
        product: pack.paddleProductId,
        email: userProfile?.email,
        passthrough: JSON.stringify({
          accountId,
          creditPack: pack.internalId,
        }),
        title: `${pack.credits} ${t('Credits', 'credits')}`,
        successCallback: () => {
          toast.success(
            t(
              'CreditsPurchasePending',
              'Payment received — your credits will appear shortly.'
            )
          );
          // Credits are granted asynchronously by the payment webhook, which can
          // land anywhere from ~1s to tens of seconds after checkout. Re-fetch
          // the balance a few times so it updates without a manual reload.
          [2000, 5000, 10000, 20000].forEach(delay =>
            setTimeout(
              () => queryClient.invalidateQueries({
                queryKey: ['billingCredits']
              }),
              delay
            )
          );
        },
      });
    } catch (e) {
      toast.error(t('SomethingWentWrong', 'Something went wrong'));
    }
  };

  return (
    <CreditDialog
      open={open}
      onClose={onClose}
      title={t('KeepTheEditorRunning', 'Keep the editor running')}
      actions={
        <Button variant="secondary" onClick={onClose}>
          {t('Close', 'Close')}
        </Button>
      }
    >
      <Columns>
        <Column>
          <ColumnHeading>
            <Icon name="arrow up" aria-hidden="true" style={{ color: 'var(--br-warning-text)' }} />
            {t('UpgradePlan', 'Upgrade plan')}
          </ColumnHeading>
          <p>
            {t(
              'UpgradePlanDescription',
              'More monthly credits that reset every period.'
            )}
          </p>
          <Spacer />
          <Button onClick={goToSubscription} style={{ width: '100%' }}>
            {t('Upgrade', 'Upgrade')}
          </Button>
        </Column>
        <Column>
          <ColumnHeading>
            <Icon name="lightning" aria-hidden="true" style={{ color: 'var(--br-warning-text)' }} />
            {t('CreditPacks', 'Credit packs')}
          </ColumnHeading>
          <p>
            {t(
              'CreditPacksDescription',
              'One-time top-ups that do not expire.'
            )}
          </p>
          {packs.map(pack => {
            const appleStoreProduct = settings.nativeProducts?.find(
              product => product.productId === appleProductIdForPack(pack)
            );
            const purchasable = useNativePurchases
              ? settings.purchaseMode === 'ios_iap' &&
                Boolean(appleStoreProduct)
              : Boolean(pack.paddleProductId && canCheckout);
            return (
              <PackRow key={pack.internalId}>
                <span>
                  {pack.credits} {t('Credits', 'credits')}
                </span>
                {purchasable ? (
                  <Button size="small" onClick={() => buyPack(pack)}>
                    {appleStoreProduct?.priceString || `$${pack.usd}`}
                  </Button>
                ) : (
                  <span title={t('ComingSoon', 'Coming soon')}>
                    <Button size="small" variant="secondary" disabled
                      aria-label={`${pack.credits} ${t('Credits', 'credits')}: ${t('ComingSoon', 'Coming soon')}`}>
                      ${pack.usd}
                    </Button>
                  </span>
                )}
              </PackRow>
            );
          })}
        </Column>
      </Columns>
    </CreditDialog>
  );
};

export default OutOfCreditsModal;
